import Joi from 'joi';

import { type EventFormValues } from '@/domain/events/types';
import { translatedMessages } from '@/i18n';
import { CommonValidationSchemas } from '@/utils/schemas';

const NS = 'domain.events.schemas';

const optionalText = (max: number) =>
  Joi.string()
    .trim()
    .max(max)
    .allow('')
    .error(translatedMessages(NS, { 'string.max': 'tooLong' }));

const dateOnly = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Mirrors the API validation (`partnerEvents.validations.ts`). `today` (YYYY-MM-DD) is the
 * earliest start date for a new event; editing keeps whatever date the event already has.
 */
export const buildEventSchema = ({ today }: { today: string | null }) =>
  Joi.object<EventFormValues>({
    bannerUrl: Joi.string().allow(''),
    name: Joi.string()
      .trim()
      .min(3)
      .max(200)
      .required()
      .error(
        translatedMessages(NS, {
          'string.empty': 'nameRequired',
          'any.required': 'nameRequired',
          'string.min': 'nameShort',
          'string.max': 'tooLong',
        }),
      ),
    eventType: optionalText(100),
    description: optionalText(5000),
    startDate: Joi.string()
      .pattern(dateOnly)
      .required()
      .custom((value: string, helpers) =>
        today && value < today ? helpers.error('date.past') : value,
      )
      .error(
        translatedMessages(NS, {
          'string.empty': 'startDateRequired',
          'any.required': 'startDateRequired',
          'string.pattern.base': 'startDateRequired',
          'date.past': 'startDatePast',
        }),
      ),
    startTime: Joi.string()
      .pattern(/^([01]\d|2[0-3]):[0-5]\d$/)
      .allow('')
      .error(translatedMessages(NS, { 'string.pattern.base': 'startTimeInvalid' })),
    recurrenceFreq: Joi.string().valid('', 'daily', 'weekly', 'monthly'),
    recurrenceWeekdays: Joi.array().items(Joi.number().integer().min(0).max(6)).unique(),
    recurrenceUntil: Joi.string()
      .allow('')
      .pattern(dateOnly)
      .custom((value: string, helpers) => {
        const { startDate } = helpers.state.ancestors[0] as EventFormValues;
        return value && startDate && value < startDate ? helpers.error('date.beforeStart') : value;
      })
      .error(
        translatedMessages(NS, {
          'string.pattern.base': 'recurrenceUntilInvalid',
          'date.beforeStart': 'recurrenceUntilBeforeStart',
        }),
      ),
    address: CommonValidationSchemas.address.required(),
    externalUrl: Joi.string()
      .trim()
      .uri({ scheme: ['http', 'https'] })
      .max(2000)
      .allow('')
      .error(
        translatedMessages(NS, {
          'string.uri': 'externalUrlInvalid',
          'string.uriCustomScheme': 'externalUrlInvalid',
          'string.max': 'tooLong',
        }),
      ),
  });
